import { Crown, Lock, X, Sparkles, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

// Realistic-looking placeholder dossier so the blur has something juicy to hide.
const SAMPLE = {
  overall: 82,
  tier: "Chad",
  celeb: "Chris Evans (78%)",
  canthal_tilt: "+4° to +5° (positive)",
  gonial_angle: "115°–118° (defined)",
  midface_ratio: "0.98 (near ideal 1.0)",
  bf: "12–14%",
  ideal_bf: "9–11%",
  ideal_weight: "84–86kg lean",
  archetype: "Warrior",
  actions: [
    { area: "Jawline", action: "Chin-tuck mewing 4×/day, 6 wk", impact: "high" },
    { area: "Skin", action: "0.05% tretinoin nightly, 12 wk", impact: "high" },
    { area: "Body Comp", action: "Recomp cut 300kcal deficit", impact: "high" },
    { area: "Style", action: "Fitted crew tees, sharper silhouette", impact: "medium" },
  ],
};

export default function EliteScanPreview({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="elite-preview-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative panel-bright max-w-4xl w-full my-8 border-2 border-[var(--primary)]"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 text-[var(--text-muted)] hover:text-white bg-black/60 border border-white/10 p-2"
          data-testid="elite-preview-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header — sharp */}
        <div className="px-6 py-4 border-b border-[var(--border-bright)] flex items-center justify-between flex-wrap gap-3">
          <div>
            <span className="tape-label" style={{ background: "var(--primary)" }}>ELITE DOSSIER · SAMPLE</span>
            <div className="font-display text-3xl mt-2">SEE WHAT VIP SEES.</div>
          </div>
          <Crown className="w-8 h-8 text-[var(--primary)] glow" />
        </div>

        {/* Blurred body — showcases every Elite-only field */}
        <div className="relative">
          <div
            className="p-6 select-none pointer-events-none"
            style={{ filter: "blur(6px)", opacity: 0.9 }}
            aria-hidden="true"
          >
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-4 text-center">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Overall Mog Score</div>
                <div className="font-display text-8xl text-[var(--primary)] glow leading-none mt-2">{SAMPLE.overall}</div>
                <div className="font-display text-2xl mt-2">{SAMPLE.tier.toUpperCase()}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-3">
                  MOG-MATCH · {SAMPLE.celeb}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--primary)] mt-1">
                  {SAMPLE.archetype.toUpperCase()}
                </div>
              </div>
              <div className="col-span-12 md:col-span-8">
                <div className="grid grid-cols-2 gap-3 text-xs font-mono">
                  <MetricRow label="Canthal tilt" v={SAMPLE.canthal_tilt} />
                  <MetricRow label="Gonial angle" v={SAMPLE.gonial_angle} />
                  <MetricRow label="Midface ratio" v={SAMPLE.midface_ratio} />
                  <MetricRow label="Body fat" v={SAMPLE.bf} />
                  <MetricRow label="Ideal BF" v={SAMPLE.ideal_bf} />
                  <MetricRow label="Ideal weight" v={SAMPLE.ideal_weight} />
                </div>
                <div className="mt-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">
                    Ranked action stack (10 items · timeframes)
                  </div>
                  <div className="space-y-1">
                    {SAMPLE.actions.map((a, i) => (
                      <div key={i} className="flex items-center gap-3 text-xs font-mono border-l-2 border-[var(--primary)] pl-3 py-1">
                        <span className="text-[var(--primary)]">#{i + 1}</span>
                        <span className="text-white">{a.area}:</span>
                        <span className="text-[var(--text-muted)] flex-1 truncate">{a.action}</span>
                        <span className="text-[var(--primary)] uppercase">{a.impact}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Glass overlay CTA */}
          <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
            <div className="pointer-events-auto bg-black/70 backdrop-blur-xl border border-[var(--primary)] px-8 py-6 text-center max-w-md w-full shadow-[0_0_60px_rgba(0,255,102,0.35)]">
              <Lock className="w-8 h-8 text-[var(--primary)] mx-auto mb-3 glow" />
              <div className="tape-label mx-auto inline-block mb-3" style={{ background: "var(--primary)" }}>
                VIP LOCKED
              </div>
              <div className="font-display text-3xl mb-2">24 SUB-METRICS</div>
              <p className="text-xs font-mono text-[var(--text-muted)] mb-5 uppercase tracking-widest">
                Celebrity mog-match · Canthal tilt in degrees · Gonial angle · Midface ratio · Ideal BF% + weight · 10 ranked actions with timeframes
              </p>
              <Link
                to="/vip"
                className="btn-brutal w-full flex items-center justify-center gap-2"
                data-testid="elite-preview-upgrade"
              >
                <Sparkles className="w-4 h-4" /> Unlock Elite from £9.99 <ArrowUpRight className="w-4 h-4" />
              </Link>
              <p className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                One-time · No subscription · Instant unlock
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const MetricRow = ({ label, v }) => (
  <div className="flex items-center justify-between gap-2 border-b border-[var(--border)] pb-1">
    <span className="text-[var(--text-muted)] uppercase text-[10px] tracking-widest">{label}</span>
    <span className="text-white">{v}</span>
  </div>
);
